import { Button, Descriptions, Drawer, Space, Typography } from 'antd';
import { ExportOutlined } from '@ant-design/icons';
import type { DoohMedia } from '@goelectric/shared-types';
import { API_BASE_URL } from '@/lib/env';
import { MEDIA_KIND_LABELS, formatBytes, formatDateTime, formatDuration, formatResolution } from '../utils/format';

export interface MediaDetailDrawerProps {
  media: DoohMedia | null;
  open: boolean;
  canManage: boolean;
  onClose: () => void;
  onDelete: (media: DoohMedia) => void;
}

/** URL pública del binario del máster (endpoint del player, sin JWT) — contrato `dooh`. */
export function mediaContentUrl(mediaId: string): string {
  return `${API_BASE_URL}/api/v1/dooh/media/${encodeURIComponent(mediaId)}/content`;
}

/** Detalle de un medio de la biblioteca (metadatos + acción de baja). */
export function MediaDetailDrawer({ media, open, canManage, onClose, onDelete }: MediaDetailDrawerProps) {
  return (
    <Drawer title="Detalle del medio" open={open} onClose={onClose} width={480}>
      {media && (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Typography.Title level={4} style={{ margin: 0, wordBreak: 'break-all' }}>
            {media.filename}
          </Typography.Title>

          <Descriptions column={1} size="small" bordered>
            <Descriptions.Item label="Tipo">{MEDIA_KIND_LABELS[media.kind]}</Descriptions.Item>
            <Descriptions.Item label="Formato">{media.mime}</Descriptions.Item>
            <Descriptions.Item label="Tamaño">{formatBytes(media.sizeBytes)}</Descriptions.Item>
            <Descriptions.Item label="Dimensiones">
              {formatResolution(media.width, media.height)}
            </Descriptions.Item>
            <Descriptions.Item label="Duración por defecto">
              {formatDuration(media.durationSeconds)}
            </Descriptions.Item>
            <Descriptions.Item label="Estado">{media.status === 'READY' ? 'Listo' : 'Dado de baja'}</Descriptions.Item>
            <Descriptions.Item label="Suma de verificación (SHA-256)">
              <Typography.Text style={{ fontFamily: 'var(--ge-font-mono)', wordBreak: 'break-all', fontSize: 12 }}>
                {media.checksum}
              </Typography.Text>
            </Descriptions.Item>
            <Descriptions.Item label="Creado">{formatDateTime(media.createdAt)}</Descriptions.Item>
            <Descriptions.Item label="Actualizado">{formatDateTime(media.updatedAt)}</Descriptions.Item>
          </Descriptions>

          <Space>
            <Button
              icon={<ExportOutlined />}
              href={mediaContentUrl(media.id)}
              target="_blank"
              rel="noreferrer"
            >
              Abrir archivo
            </Button>
            {canManage && (
              <Button danger onClick={() => onDelete(media)}>
                Eliminar
              </Button>
            )}
          </Space>
        </Space>
      )}
    </Drawer>
  );
}
